/* Writing a workspace's own skill (tasks/custom-skills/01-design-mock.md): check a
   folder, save it, save a new version, delete it, copy one from a GitHub link. Only the
   Skills page writes skills, so these live with it; reading the catalog is
   `shared/api/skills.ts`. A folder goes up as multipart (`folderForm`). */
import { client } from "@/shared/api/client";
import { unwrap, type Envelope } from "@/shared/api/envelope";

import type { GithubImport, SkillCheck, SkillChoice, SkillDetail } from "@/shared/types";

/* A folder is read whole on the server; the default 15 s is short for a large zip. */
const UPLOAD_TIMEOUT = 120_000;

/** What the checker finds in a folder. Nothing is stored. */
export async function checkSkill(form: FormData): Promise<SkillCheck> {
  return unwrap(await client.post<Envelope<SkillCheck>>("/skills/check", form, { timeout: UPLOAD_TIMEOUT }));
}

/** A new workspace skill. A 422 carries every problem as `details` ({path, issue, code}). */
export async function createSkill(form: FormData): Promise<SkillDetail> {
  return unwrap(await client.post<Envelope<SkillDetail>>("/skills", form, { timeout: UPLOAD_TIMEOUT }));
}

/** A new version; `form` holds `base_version`, the one the editor opened. */
export async function updateSkill(id: string, form: FormData): Promise<SkillDetail> {
  return unwrap(await client.put<Envelope<SkillDetail>>(`/skills/${id}`, form, { timeout: UPLOAD_TIMEOUT }));
}

export async function deleteSkill(id: string): Promise<void> {
  await client.delete(`/skills/${id}`);
}

/** A 201 is the saved skill; a 200 lists the link's skills to pick one from. */
export async function importFromGithub(url: string, folder?: string): Promise<GithubImport> {
  const res = await client.post<Envelope<SkillDetail | { choices: SkillChoice[] }>>("/skills/github", { url, folder }, { timeout: UPLOAD_TIMEOUT });
  const data = unwrap(res);
  return res.status === 201 ? { skill: data as SkillDetail } : (data as { choices: SkillChoice[] });
}
